import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { TemplateRef, ViewContainerRef } from '@angular/core';
import { filter } from 'rxjs/operators';

// --- Shared constants ---

export const DAY_OPTIONS = [
  { value: 'monday', label: 'Mon' },
  { value: 'tuesday', label: 'Tue' },
  { value: 'wednesday', label: 'Wed' },
  { value: 'thursday', label: 'Thu' },
  { value: 'friday', label: 'Fri' },
  { value: 'saturday', label: 'Sat' },
  { value: 'sunday', label: 'Sun' },
];

export const DAY_MAP: { [key: string]: string } = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed',
  thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun'
};

export const REVERSE_DAY_MAP: { [key: string]: string } = {
  Mon: 'monday', Tue: 'tuesday', Wed: 'wednesday',
  Thu: 'thursday', Fri: 'friday', Sat: 'saturday', Sun: 'sunday'
};

export const DAY_NUMBERS: { [key: string]: number } = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
  thursday: 4, friday: 5, saturday: 6,
};

export const TIME_OPTIONS: { value: string; label: string }[] = (() => {
  const opts: { value: string; label: string }[] = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 15, 30, 45]) {
      const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const period = h >= 12 ? 'PM' : 'AM';
      const label = `${displayH}:${String(m).padStart(2, '0')} ${period}`;
      opts.push({ value: val, label });
    }
  }
  return opts;
})();

// --- Formatter functions ---

export function formatTime(time: string): string {
  if (!time) return '';
  const parts = time.split(':');
  let hours = parseInt(parts[0], 10);
  const minutes = parts[1] || '00';
  const period = hours >= 12 ? 'PM' : 'AM';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

export function formatDurationShort(dur: string): string {
  if (!dur) return '';
  if (dur.includes(':')) {
    const parts = dur.split(':');
    const hours = parseInt(parts[0], 10);
    const mins = parseInt(parts[1], 10);
    return String(hours * 60 + mins);
  }
  return dur;
}

export function formatEventDate(dateStr: string): string {
  if (!dateStr) return '';
  const datePart = dateStr.substring(0, 10);
  const d = new Date(datePart + 'T12:00:00');
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(d);
}

export function toLocalDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function getInstructorName(uid: string, fieldsService: any): string {
  if (!uid) return 'Unassigned';
  const instructors = fieldsService.instructorData();
  const found = instructors?.find((i: any) => String(i.uid) === String(uid));
  return found ? found.value : `Instructor #${uid}`;
}

// --- Preview date calculation ---

export interface PreviewDateConfig {
  days: string[];
  time: string;
  duration: string;
  instructorId: string;
  alternateInstructors: string[];
  repetitionUnits: string;
  repetitionFrequency: number;
  endMode: string;
  expirationDate: string;
  startDate?: string;
  enrollmentLessonsAvailable?: number;
  dateOverrides?: { [date: string]: { time?: string; instructor?: string } };
  dayOverrides?: { [day: string]: { time?: string; instructor?: string; duration?: string } };
  fieldsService: any;
}

export interface PreviewEvent {
  date: string;
  day: string;
  dayKey: string;
  time: string;
  instructor: string;
  instructorId: string;
  duration: string;
  id?: number;
  statusTid?: string;
}

export function calculatePreviewDates(config: PreviewDateConfig): PreviewEvent[] {
  const { days, time, duration, instructorId, alternateInstructors,
          repetitionUnits, repetitionFrequency, endMode, expirationDate,
          startDate, enrollmentLessonsAvailable, dateOverrides, dayOverrides,
          fieldsService } = config;

  if (days.length === 0) return [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // The earliest date we'll generate events for
  let start = new Date(today);
  if (startDate) {
    const sd = new Date(startDate + 'T12:00:00');
    if (sd > today) start = sd;
  }

  // The anchor date for week cadence (used for EOW/multi-week alignment)
  // Must use the original series start date to match backend rhythm
  let cadenceAnchor = new Date(start);
  if (startDate && startDate.length >= 10) {
    const anchor = new Date(startDate.substring(0, 10) + 'T12:00:00');
    if (!isNaN(anchor.getTime())) {
      cadenceAnchor = anchor;
    }
  }

  let end: Date;
  let maxEvents = 100;
  if (endMode === 'date' && expirationDate) {
    end = new Date(expirationDate + 'T23:59:59');
  } else if (endMode === 'enrollment') {
    end = new Date(today);
    if (enrollmentLessonsAvailable && enrollmentLessonsAvailable > 0) {
      end.setFullYear(end.getFullYear() + 2);
      maxEvents = enrollmentLessonsAvailable;
    } else {
      // When enrollment lesson count is unknown (e.g. editing in dialog),
      // show a 6-month preview window; actual count is enforced by the backend
      end.setMonth(end.getMonth() + 6);
    }
  } else {
    // indefinite
    end = new Date(today);
    end.setMonth(end.getMonth() + 6);
  }

  const useRotation = alternateInstructors.length > 0;
  const instructorPool: string[] = [instructorId || ''];
  if (useRotation) {
    instructorPool.push(...alternateInstructors);
  }

  const results: (PreviewEvent & { _dayKey?: string })[] = [];
  const isEow = repetitionUnits === 'eow';
  const isMonthly = repetitionUnits === 'monthly';
  const weekInterval = isEow ? 2 : (repetitionFrequency || 1);

  if (isMonthly) {
    let cursor = new Date(start);
    while (cursor <= end && results.length < maxEvents) {
      for (const day of days) {
        if (results.length >= maxEvents) break;
        const targetDow = DAY_NUMBERS[day];
        const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
        let d = new Date(monthStart);
        while (d.getDay() !== targetDow) d.setDate(d.getDate() + 1);
        while (d < start) d.setDate(d.getDate() + 7);
        if (d.getMonth() === monthStart.getMonth() && d <= end) {
          const dateStr = toLocalDateString(d);
          const dateOvr = dateOverrides?.[dateStr];
          const dayOvr = dayOverrides?.[day];
          results.push({
            date: dateStr,
            day: DAY_MAP[day] || day,
            dayKey: day,
            time: formatTime(dateOvr?.time || dayOvr?.time || time || ''),
            instructor: '',
            instructorId: '',
            duration: formatDurationShort(dayOvr?.duration || duration || ''),
            _dayKey: dayOvr?.instructor ? day : undefined,
          });
        }
      }
      cursor.setMonth(cursor.getMonth() + 1);
      cursor.setDate(1);
    }
  } else {
    const sortedDays = [...days].sort((a, b) => (DAY_NUMBERS[a] || 0) - (DAY_NUMBERS[b] || 0));

    // Anchor the week cadence to the original start date so EOW/multi-week
    // rhythm matches the backend, then advance to the first week >= today
    let weekStart = new Date(cadenceAnchor);
    const anchorDow = weekStart.getDay();
    const diffToMon = (anchorDow === 0 ? -6 : 1 - anchorDow);
    weekStart.setDate(weekStart.getDate() + diffToMon);

    // Advance to the first week that could contain events >= start
    // Use the end of the week (Sunday) to decide if we've reached start yet
    if (weekStart < start) {
      while (true) {
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        if (weekEnd >= start) break;
        weekStart.setDate(weekStart.getDate() + 7 * weekInterval);
      }
    }

    while (weekStart <= end && results.length < maxEvents) {
      for (const day of sortedDays) {
        if (results.length >= maxEvents) break;
        const targetDow = DAY_NUMBERS[day];
        const offset = targetDow === 0 ? 6 : targetDow - 1;
        const eventDate = new Date(weekStart);
        eventDate.setDate(eventDate.getDate() + offset);

        if (eventDate >= start && eventDate <= end) {
          const dateStr = toLocalDateString(eventDate);
          const dateOvr = dateOverrides?.[dateStr];
          const dayOvr = dayOverrides?.[day];
          results.push({
            date: dateStr,
            day: DAY_MAP[day] || day,
            dayKey: day,
            time: formatTime(dateOvr?.time || dayOvr?.time || time || ''),
            instructor: '',
            instructorId: '',
            duration: formatDurationShort(dayOvr?.duration || duration || ''),
            _dayKey: dayOvr?.instructor ? day : undefined,
          });
        }
      }
      weekStart.setDate(weekStart.getDate() + 7 * weekInterval);
    }
  }

  results.sort((a, b) => a.date.localeCompare(b.date));

  // Assign instructors
  let rotationIndex = 0;
  for (const evt of results) {
    const dateOvr = dateOverrides?.[evt.date];
    if (dateOvr?.instructor) {
      evt.instructorId = dateOvr.instructor;
      evt.instructor = getInstructorName(dateOvr.instructor, fieldsService);
      // Override still consumes a rotation slot so subsequent events stay aligned
      if (useRotation) rotationIndex++;
    } else if (evt._dayKey && dayOverrides?.[evt._dayKey]?.instructor) {
      evt.instructorId = dayOverrides[evt._dayKey].instructor!;
      evt.instructor = getInstructorName(dayOverrides[evt._dayKey].instructor!, fieldsService);
      if (useRotation) rotationIndex++;
    } else if (useRotation) {
      const uid = instructorPool[rotationIndex % instructorPool.length];
      evt.instructorId = uid;
      evt.instructor = getInstructorName(uid, fieldsService);
      rotationIndex++;
    } else {
      evt.instructorId = instructorId || '';
      evt.instructor = getInstructorName(instructorId || '', fieldsService);
    }
    delete (evt as any)._dayKey;
  }

  return results;
}

// --- CDK overlay helper ---

export function createPopoverOverlay(
  overlay: Overlay,
  origin: HTMLElement,
  template: TemplateRef<any>,
  viewContainerRef: ViewContainerRef,
  options?: { width?: number; onClose?: () => void }
): OverlayRef {
  const positionStrategy = overlay.position()
    .flexibleConnectedTo(origin)
    .withPositions([
      { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
      { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
      { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 },
    ])
    .withPush(true)
    .withViewportMargin(8);

  const scrollStrategy = overlay.scrollStrategies.reposition();

  const overlayRef = overlay.create({
    positionStrategy,
    scrollStrategy,
    hasBackdrop: true,
    backdropClass: 'event-popover-backdrop',
    panelClass: 'event-popover-panel',
    width: options?.width || 300,
  });

  const onCloseHandler = options?.onClose || (() => {});
  overlayRef.backdropClick().subscribe(() => onCloseHandler());
  overlayRef.keydownEvents()
    .pipe(filter((e: KeyboardEvent) => e.key === 'Escape'))
    .subscribe(() => onCloseHandler());

  const portal = new TemplatePortal(template, viewContainerRef);
  overlayRef.attach(portal);

  return overlayRef;
}
